import { SUBSCRIPTION_STATUS } from "@/libraries/constant";
import { z } from "zod";

export const DataIdSchema = z.number().int();

export const AuthLoginSchema = z.object({
	email: z.string().email(),
	password: z.string().min(9, "Password must be at least 9 characters long"),
});

export type AuthLoginSchema = z.infer<typeof AuthLoginSchema>;

export const AuthPasswordSchema = z
	.string()
	.min(9, "Minimum 9 characters")
	.regex(/[a-z]/, "Contains at least 1 lowercase character")
	.regex(/[A-Z]/, "Contains at least 1 uppercase character")
	.regex(/\d/, "Contains at least 1 digit")
	.regex(/[@$!%*?&]/, "Contains at least 1 special character");

export type AuthPasswordSchema = z.infer<typeof AuthPasswordSchema>;

export const AuthRegisterCredentialsBaseSchema = z.object({
	email: z.string().email("Invalid email address"),
	password: AuthPasswordSchema,
	password_confirmation: z.string().min(1, "Confirm password is required"),
});

export type AccountSettingChangeEmailFieldsSchema = z.infer<
	typeof AccountSettingChangeEmailFieldsSchema
>;

export const AccountSettingChangeEmailFieldsSchema =
	AuthRegisterCredentialsBaseSchema.pick({
		email: true,
	});
export type AuthRegisterCredentialsBaseSchema = z.infer<
	typeof AuthRegisterCredentialsBaseSchema
>;

export const AccountSettingChangePasswordFieldsSchema = z.object({
	current_password: z.string().min(1, "Current password is required"),
});
export type AccountSettingChangePasswordFieldsSchema = z.infer<
	typeof AccountSettingChangePasswordFieldsSchema
>;

export const AccountSettingChangePhoneNumberFieldsSchema = z.object({
	phone_number: z
		.string()
		.min(1, "Phone number is required")
		.regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number"),
});

export type AccountSettingChangePhoneNumberFieldsSchema = z.infer<
	typeof AccountSettingChangePhoneNumberFieldsSchema
>;

export const AccountSettingDeleteAccountFieldsSchema = z.object({
	understand: z.boolean().default(false),
});

export type AccountSettingDeleteAccountFieldsSchema = z.infer<
	typeof AccountSettingDeleteAccountFieldsSchema
>;

export const AuthChangePasswordSchema = AuthRegisterCredentialsBaseSchema.omit({
	email: true,
}).refine((data) => data.password === data.password_confirmation, {
	path: ["password_confirmation"],
	message: "The password you entered doesn’t match",
});

export type AuthChangePasswordSchema = z.infer<typeof AuthChangePasswordSchema>;

export const AccountSettingVerifyPhoneNumberFieldsSchema = z.object({
	otp: z.string(),
});

export type AccountSettingVerifyPhoneNumberFieldsSchema = z.infer<
	typeof AccountSettingVerifyPhoneNumberFieldsSchema
>;

export const SearchParamsAccountSettingVerifyPhoneNumber = z.object({
	phone_number: z.string(),
	otp: z.string().optional(),
});

export type SearchParamsAccountSettingVerifyPhoneNumber = z.infer<
	typeof SearchParamsAccountSettingVerifyPhoneNumber
>;

export const ProfessionalCredentialFieldsSchema = z.object({
	country: z.string().min(1, "Field is required"),
	profession: z.string().min(1, "Field is required"),
	full_name: z.string().min(1, "Field is required"),
	workplace: z.string().min(1, "Field is required"),
	registration: z.string().min(1, "Field is required"),
	referral_code: z.string().optional(),
});

export type ProfessionalCredentialFieldsSchema = z.infer<
	typeof ProfessionalCredentialFieldsSchema
>;

export const ProfessionalInformationFieldsSchema = z.object({
	job_title: z.string().min(1, "Field is required"),
	qualifications: z.string().min(1, "Field is required"),
	specialization: z.string().min(1, "Field is required"),
	directory: z.boolean().default(false),
});

export type ProfessionalInformationFieldsSchema = z.infer<
	typeof ProfessionalInformationFieldsSchema
>;

export const AuthRegisterCredentialsSchema =
	AuthRegisterCredentialsBaseSchema.refine(
		(data) => data.password === data.password_confirmation,
		{
			path: ["password_confirmation"],
			message: "The password you entered doesn’t match",
		},
	);

export type AuthRegisterCredentialsSchema = z.infer<
	typeof AuthRegisterCredentialsSchema
>;

export const AuthWebviewSchema = z.object({
	token: z.string().optional(),
	reset_password_token: z.string().optional(),
	redirect: z.string().optional(),
});

export type AuthWebviewSchema = z.infer<typeof AuthWebviewSchema>;

export const PhoneNumbersSchema = z
	.string()
	.min(1, "Phone number is required")
	.regex(/^\+(\d{1,4})[\s-]?([\d\s()-]{6,15})$/, "Invalid phone number");

export const AuthRegisterVerifyMobileSchema = z.object({
	phone_number: z.string().min(1, "Phone number is required"),
	dial_code: z.string().min(1, "Dial code is required"),
	country_name: z.string().min(1, "Country name is required"),
	code: z.string().min(1, "Country code is required"),
	verify_code: z
		.string()
		.min(4, "Verification code is required")
		.transform((v) => v.replace(/\D/g, "")),
});

export type AuthRegisterVerifyMobileSchema = z.infer<
	typeof AuthRegisterVerifyMobileSchema
>;

export const PostInvitationBodySchema = z.object({
	inviteable_type: z.enum(["Team", "Group", "Case"]),
	inviteable_id: z.number().int(),
	message_id: z.number().int(),
	status: z.enum(["accept", "decline"]),
});

export type PostInvitationBodySchema = z.infer<typeof PostInvitationBodySchema>;

export const LoginSsoPayloadSchema = z.object({
	username: z.string().min(1, "Username is required"),
	password: z.string().min(1, "Password is required"),
});
export type LoginSsoPayloadSchema = z.infer<typeof LoginSsoPayloadSchema>;

export const SubscriptionSearchSchema = z.object({
	plan: z.nativeEnum(SUBSCRIPTION_STATUS).optional(),
});
export type SubscriptionSearchSchema = z.infer<typeof SubscriptionSearchSchema>;

export const VerificationSearchParamsSchema = AuthWebviewSchema;
export type VerificationSearchParamsSchema = z.infer<
	typeof VerificationSearchParamsSchema
>;
